import { useCallback, useMemo } from "react";
import { CoverageProvider, useCoverage } from "./context/CoverageContext";
import { HubLandscapeGrid } from "./components/HubLandscapeGrid";
import { HubDetailPanel } from "./components/HubDetailSheet";
import { CoverageFilterBar } from "./components/CoverageFilterBar";
import { ProductivityChart } from "./components/ProductivityChart";
import { HubWorkFilesPanel } from "./components/HubWorkFilesPanel";
import { VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import {
  MarketingStackWorkflowStrip,
  marketingStackCardClassName,
  marketingStackContentInsetClassName,
  marketingStackPageClassName,
} from "@/react/components/marketingStack";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/modern/components/ui/resizable";
import { AsyncTaskUI } from "@/modern/components/async-tasks/async-task-ui";
import { useAsyncTaskContext } from "@/modern/components/async-tasks/context/AsyncTaskContext";
import type { AsyncTask } from "@/modern/components/async-tasks/types";
import {
  isAsyncTaskType,
  matchBoardMarketingWorkFileGenerationTask,
} from "@/modern/components/async-tasks/utils";
import { ASYNC_TASK_TYPES } from "@/other/constants/OtherKeys.jsx";
import { showToast } from "@/other/utils/utils";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";

const CoverageViewContent = () => {
  const { selectedHub } = useCoverage();

  return (
    <div className={`coverage-page ${marketingStackPageClassName} w-full overflow-hidden bg-background`}>
      <MarketingStackWorkflowStrip activeView={VIEW_TYPES.coverage} />
      <div className={marketingStackContentInsetClassName}>
        <div className={marketingStackCardClassName()}>
          <div className="shrink-0 border-b border-border p-5">
            <ProductivityChart />
          </div>

          <div className="flex min-h-0 flex-1 p-3">
            <ResizablePanelGroup orientation="horizontal" className="min-h-0 flex-1 min-w-0">
              <ResizablePanel id="coverage-hub-list" maxSize={"75%"}>
                <div className="flex h-full flex-col overflow-hidden pr-2.5">
                  <div className="shrink-0">
                    <CoverageFilterBar />
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto">
                    <HubLandscapeGrid />
                  </div>
                </div>
              </ResizablePanel>

              {selectedHub && (
                <>
                  <ResizableHandle withHandle />
                  <ResizablePanel
                    id="coverage-detail"
                    defaultSize={"30%"}
                    maxSize={"55%"}
                  >
                    <div className="ml-3.5 flex h-full flex-col overflow-hidden rounded-xl border border-border">
                      <HubDetailPanel />
                    </div>
                  </ResizablePanel>

                  <ResizableHandle withHandle />
                  <ResizablePanel
                    id="coverage-workfiles"
                    defaultSize={"22%"}
                    maxSize={"40%"}
                  >
                    <div className="ml-3.5 flex h-full flex-col overflow-hidden rounded-xl border border-border">
                      <HubWorkFilesPanel />
                    </div>
                  </ResizablePanel>
                </>
              )}
            </ResizablePanelGroup>
          </div>
        </div>
      </div>
    </div>
  );
};

function CoverageMarketingAsyncShell() {
  const { selectedBoard } = useSwivuBryntumContext();
  const { fetchResult } = useAsyncTaskContext();
  const { selectedHub, invalidateAfterWorkFile } = useCoverage();
  const boardId = (selectedBoard?.record as { id?: number } | undefined)?.id;

  const taskFilter = useCallback(
    (task: AsyncTask) =>
      boardId != null && matchBoardMarketingWorkFileGenerationTask(task, boardId),
    [boardId]
  );

  const asyncEffects = useMemo(
    () => ({
      onCompleted: (task: AsyncTask) => {
        void (async () => {
          if (isAsyncTaskType(task, ASYNC_TASK_TYPES.MARKETING_WORK_FILE_GENERATION)) {
            showToast({
              msg: "Work file ready.",
              type: "success",
              duration: 3000,
            });
            const hubId = selectedHub ? Number(selectedHub.id) : null;
            if (hubId != null) invalidateAfterWorkFile(hubId);
          }

          try {
            await fetchResult(task.id);
          } catch {
            // Task may already be retrieved.
          }
        })();
      },
      onFailed: (task: AsyncTask) => {
        void (async () => {
          if (isAsyncTaskType(task, ASYNC_TASK_TYPES.MARKETING_WORK_FILE_GENERATION)) {
            showToast({
              msg: "Work file generation failed.",
              type: "error",
              duration: 3000,
            });
          }

          try {
            await fetchResult(task.id);
          } catch {
            // Task may already be retrieved.
          }
        })();
      },
    }),
    [fetchResult, selectedHub, invalidateAfterWorkFile]
  );

  if (boardId == null) {
    return null;
  }

  return (
    <AsyncTaskUI filter={taskFilter} effects={asyncEffects}>
      <CoverageViewContent />
    </AsyncTaskUI>
  );
}

const CoverageView = () => {
  return (
    <CoverageProvider>
      <CoverageMarketingAsyncShell />
    </CoverageProvider>
  );
};

export default CoverageView;
